import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { Banner, Button, Skeleton, useToast } from '../../../../ds/components';
import { errorMessage } from '../../../../lib/api';
import { OrganizationPage, QueryError } from '../users/OrganizationPage';
import {
  usePermissionMatrix,
  useResetUserPermissions,
  useSaveUserPermissions,
  useUserPermissions,
  type UserPermissionRow,
} from './api';
import { PermissionMatrix, type MatrixRowView } from './PermissionMatrix';
import styles from './PermissionsPage.module.css';

/**
 * Admin 02 „Rechte · <Kurzname>": the role matrix in which the user's role column carries his effective values —
 * deviations from the template as accent chips, editable — with the banner of the server („2 Abweichungen, beide
 * heikel …"). „Speichern" → `PUT /users/:id/permissions`, „Auf Vorlage zurücksetzen" → `POST …/reset`; a sales
 * lead's sensitive change becomes a request whose text the server returns.
 */
export function UserPermissionsPage() {
  const { userId = '' } = useParams();
  const { toast } = useToast();
  const matrix = usePermissionMatrix();
  const user = useUserPermissions(userId);
  const save = useSaveUserPermissions(userId);
  const reset = useResetUserPermissions(userId);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const data = user.data;
  const roleId = data?.user.roleId ?? null;
  const editable = data?.actions.save.allowed ?? false;

  const userRows = useMemo(() => {
    const map = new Map<string, UserPermissionRow>();
    for (const category of data?.categories ?? []) for (const row of category.rows) map.set(row.key, row);
    return map;
  }, [data]);

  const categories = useMemo(() => {
    if (!matrix.data || !data) return [];
    return matrix.data.categories.map((category) => ({
      label: category.label,
      rows: category.rows.map<MatrixRowView>((row) => {
        const own = userRows.get(row.key);
        return {
          key: row.key,
          label: row.label,
          sensitive: row.sensitive,
          options: row.options,
          cells: row.cells.map((cell) => {
            if (cell.roleId !== roleId || !own) {
              return {
                roleId: cell.roleId,
                value: cell.value,
                label: cell.label,
                editable: false,
                highlighted: false,
              };
            }
            const changed = draft[row.key];
            const value = changed ?? own.effective.value;
            return {
              roleId: cell.roleId,
              value,
              label: row.options.find((option) => option.value === value)?.label ?? own.effective.label,
              editable,
              highlighted: changed !== undefined || (own.deviates && value !== own.template.value),
              pendingLabel: own.pending?.label ?? null,
              lockedReason: editable ? null : data.actions.save.reason,
            };
          }),
        };
      }),
    }));
  }, [matrix.data, data, userRows, roleId, draft, editable]);

  function change(_roleId: string, key: string, value: string) {
    const own = userRows.get(key);
    setDraft((current) => {
      const next = { ...current };
      if (own && value === own.effective.value) delete next[key];
      else next[key] = value;
      return next;
    });
  }

  function submit() {
    const overrides = Object.entries(draft).map(([key, value]) => ({ key, value }));
    save.mutate(overrides, {
      onSuccess: () => {
        setDraft({});
        toast({ message: 'Rechte gespeichert.' });
      },
    });
  }

  function resetToTemplate() {
    reset.mutate(undefined, {
      onSuccess: () => {
        setDraft({});
        toast({ message: 'Die Rechte folgen wieder der Vorlage.' });
      },
    });
  }

  const deviationCount = [...userRows.values()].filter((row) => row.deviates).length;
  const failure = save.error ?? reset.error;

  return (
    <OrganizationPage
      variant="admin"
      title={data?.title ?? 'Rechte'}
      description={data?.subtitle}
      actions={
        data ? (
          <>
            <Button
              variant="secondary"
              disabled={
                !data.actions.reset.allowed || (deviationCount === 0 && Object.keys(draft).length === 0)
              }
              loading={reset.isPending}
              title={data.actions.reset.reason ?? undefined}
              onClick={resetToTemplate}
            >
              Auf Vorlage zurücksetzen
            </Button>
            <Button
              disabled={!editable || Object.keys(draft).length === 0}
              loading={save.isPending}
              title={data.actions.save.reason ?? undefined}
              onClick={submit}
            >
              Speichern
            </Button>
          </>
        ) : null
      }
    >
      {user.isError ? <QueryError error={user.error} /> : null}
      {matrix.isError && !user.isError ? <QueryError error={matrix.error} /> : null}
      {failure ? <Banner tone="danger">{errorMessage(failure)}</Banner> : null}
      {data && !editable && data.actions.save.reason ? (
        <Banner tone="info">{data.actions.save.reason}</Banner>
      ) : null}
      {data?.banner ? (
        <div className={styles.banner} role="status">
          <span className={styles.bannerTitle}>{data.banner.title}</span>
          {data.banner.text}
        </div>
      ) : null}
      {user.isPending || matrix.isPending ? (
        <div className={styles.skeleton} aria-busy="true">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <Skeleton key={index} variant="block" height={36} />
          ))}
        </div>
      ) : null}
      {matrix.data && data ? (
        <PermissionMatrix roles={matrix.data.roles} categories={categories} onChange={change} />
      ) : null}
    </OrganizationPage>
  );
}
